import { useState, useCallback, useEffect } from 'react';
import { authApi } from '../api/auth';
import type { User, LoginCredentials } from '../types/auth';

export interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  /** Загрузка сессии при старте приложения */
  isLoading: boolean;
  /** Идёт отправка формы входа */
  isLoggingIn: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSession = useCallback(async () => {
    try {
      const session = await authApi.getCurrentSession();
      setUser(session?.user ?? null);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setError(null);
      setIsLoggingIn(true);
      try {
        const session = await authApi.login(credentials);
        setUser(session.user);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Ошибка входа';
        setError(message);
        throw e;
      } finally {
        setIsLoggingIn(false);
      }
    },
    []
  );

  const logout = useCallback(async () => {
    setError(null);
    try {
      await authApi.logout();
      localStorage.removeItem('chat_thread_id');
      setUser(null);
    } catch {
      localStorage.removeItem('chat_thread_id');
      setUser(null);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    isLoggingIn,
    error,
    login,
    logout,
    clearError,
  };
}
